import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';

interface SolutionArtifactViewerProps {
  artifact: Artifact;
  // Trailmap L2 (spec §6.1a): see IdeaArtifactViewer for rationale.
  headingShift?: number;
}

export const SolutionArtifactViewer: React.FC<SolutionArtifactViewerProps> = ({ artifact, headingShift = 0 }) => {
  const { darkMode } = useTheme();
  const NameTag = `h${Math.min(6, 3 + headingShift)}` as 'h3' | 'h4' | 'h5' | 'h6';

  const getSolutionContent = () => {
    if (typeof artifact.content === 'string') return { content: artifact.content };
    return artifact.content;
  };

  const solution = getSolutionContent();

  return (
    <div className="solution-artifact-viewer">
      <div className={`border rounded-lg p-6 ${
        darkMode ? 'bg-indigo-900/20 border-indigo-700' : 'bg-indigo-50 border-indigo-200'
      }`}>
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-2">✅</span>
          <NameTag className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {artifact.name}
          </NameTag>
        </div>

        {solution.summary && (
          <div className={`mb-4 p-3 rounded border ${
            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <h4 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Summary
            </h4>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{solution.summary}</p>
          </div>
        )}

        <div className={`prose prose-sm max-w-none mb-4 ${darkMode ? 'prose-invert' : ''}`}>
          {typeof solution.content === 'string' ? (
            <div className={`whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {solution.content}
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: solution.html || '' }} />
          )}
        </div>

        {solution.components?.length > 0 && (
          <div className="mt-6">
            <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Solution Components
            </h4>
            <div className="space-y-4">
              {solution.components.map((component: any, index: number) => (
                <div key={index} className={`p-4 border rounded-lg ${
                  darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <h5 className={`font-medium mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    {component.title}
                  </h5>
                  <p className={`mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {component.content}
                  </p>
                  {component.source_ideas?.length > 0 && (
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="font-medium">Source Ideas:</span>
                      <ul className="list-disc list-inside mt-1">
                        {component.source_ideas.map((idea: string, i: number) => (
                          <li key={i}>{idea}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {solution.next_steps?.length > 0 && (
          <div className="mt-6">
            <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Next Steps
            </h4>
            <ul className={`text-sm list-disc pl-5 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {solution.next_steps.map((step: string, index: number) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
